import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { holderPhrase, roleNameProblem, type RoleHolder, type RoleWriteResult } from '@/lib/roleLifecycle';
import { RoleLifecycleError, createAccountRole, renameAccountRole } from '@/services/roleLifecycleService';

/**
 * RBAC-3.2 · FR-1, FR-3. Create a role, or rename and re-describe one the account owns.
 *
 * Mounted by `RolesPermissionsPage`: "New role" opens it empty, "Rename" on an own role opens it
 * filled. Never offered on a Joules role (FR-5); the database refuses that anyway (42501).
 * The name rule is RBAC-1.1's trigger; its refusal (23505) is shown as the server wrote it.
 */
export interface RoleDetailsDialogProps {
  open: boolean;
  /** null: create. Otherwise the own role being renamed. */
  role: { id: number; name: string; description: string | null } | null;
  onOpenChange: (open: boolean) => void;
  onSaved: (result: RoleWriteResult) => void;
}

export function RoleDetailsDialog({ open, role, onOpenChange, onSaved }: RoleDetailsDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [holders, setHolders] = useState<RoleHolder[]>([]);

  // Reset only when the dialog opens or switches role, never because the list refetched behind it.
  const roleId = role?.id ?? null;
  useEffect(() => {
    if (!open) return;
    setName(role?.name ?? '');
    setDescription(role?.description ?? '');
    setError(null);
    setHolders([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roleId]);

  const creating = role === null;

  const save = async () => {
    const problem = roleNameProblem(name);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(null);
    setHolders([]);
    try {
      const result = creating
        ? await createAccountRole(name.trim(), description)
        : await renameAccountRole(role.id, name.trim(), description);
      onSaved(result);
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The role could not be saved.');
      if (e instanceof RoleLifecycleError) setHolders(e.holders);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{creating ? 'New role' : `Rename "${role.name}"`}</DialogTitle>
          <DialogDescription>
            {creating
              ? 'The role belongs to this account and starts with no permissions.'
              : 'People on this role keep it. Only its name and description change.'}
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="role-details-name">Name</Label>
            <Input
              id="role-details-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Estimator"
              disabled={saving}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="role-details-description">Description (optional)</Label>
            <Textarea
              id="role-details-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What people in this role do"
              rows={2}
              disabled={saving}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
          {holders.length > 0 && (
            <ul className="space-y-1 text-sm" aria-label="Named by">
              {holders.map((h) => (
                <li key={h.noun}>
                  {h.href ? (
                    <Link to={h.href} className="text-primary underline-offset-2 hover:underline">
                      {holderPhrase(h)}
                    </Link>
                  ) : (
                    holderPhrase(h)
                  )}
                </li>
              ))}
            </ul>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {creating ? 'Create role' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default RoleDetailsDialog;
